import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, finalize, map, retry, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import type {
  Entitlements,
  EntitlementScope,
  EntitlementsToken,
} from '@ninety/platform/models/entitlements/entitlements-model';
import { TokenService } from '@ninety/platform/services/services/token.service';
import { PLATFORM_LOGGER } from '@ninety/platform/services/tokens/platform-logger.token';
import { environment } from '@ninety/ui/web/environments';

import { selectIsBillingV3Company } from '../company/company-state.selectors';

@Injectable({
  providedIn: 'root',
})
export class EntitlementsService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);

  private readonly apiBasePath = environment.entitlementsApiOrigin;
  private readonly EXPIRATION_BUFFER_MINS = 5;
  /**
   * The single in-flight token fetch, shared by all concurrent callers and held until it settles.
   * Sharing with `refCount: false` keeps the request alive when a caller unsubscribes, which
   * `EntitlementsStateEffects.init$` does via `switchMap` on a second `init` action.
   */
  private inFlightFetch$: Observable<string> | null = null;
  private readonly logger = inject(PLATFORM_LOGGER);

  clearEntitlementsToken(): void {
    this.tokenService.removeEntitlementsToken();
  }

  fetchEntitlementsToken(authToken = this.tokenService.getAccessToken()): Observable<string> {
    if (this.inFlightFetch$) return this.inFlightFetch$;

    const url = `${this.apiBasePath}/v1/entitlements-token`;
    const headers = this.getHeaders(authToken);
    const fetch$ = this.http.get<string>(url, { headers, withCredentials: true }).pipe(
      retry(3),
      tap({
        next: entitlementsToken => {
          if (entitlementsToken) {
            this.tokenService.setEntitlementsToken(entitlementsToken);
          } else {
            this.logger.warn({ message: 'entitlements_fetch_empty', data: { detail: 'Fetch returned falsy token' } });
          }
        },
        error: () => {
          this.logger.error({ message: 'entitlements_fetch_failed', data: { detail: 'Fetch failed after retries' } });
        },
      }),
      catchError(() => of('')),
      // Guarded by identity so a settling older fetch can never clear a newer one's slot.
      finalize(() => {
        if (this.inFlightFetch$ === fetch$) this.inFlightFetch$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.inFlightFetch$ = fetch$;
    return fetch$;
  }

  getEntitlementsToken(): string {
    const entitlementsTokenDecoded = this.tokenService.getEntitlementsTokenDecoded();
    const shallExpire = this.tokenService.isExpiredWithinMins(entitlementsTokenDecoded, this.EXPIRATION_BUFFER_MINS);
    if (!entitlementsTokenDecoded || shallExpire) {
      return '';
    }

    return this.tokenService.getEntitlementsToken();
  }

  /**
   * Retrieve current entitlements token if unexpired within the expiration buffer tolerance
   * Otherwise retrieve a new token
   */
  getOrFetchEntitlementsToken(authToken = this.tokenService.getAccessToken()): Observable<string> {
    return combineLatest([this.store.select(selectIsBillingV3Company)]).pipe(
      take(1),
      switchMap(([isBillingV3Company]) => {
        const isCreateCompany = this.router.url?.startsWith('/create-company');
        const isEnabled = isBillingV3Company && !isCreateCompany;
        if (!isEnabled) {
          this.logger.warn({
            message: 'entitlements_skipped_disabled',
            data: {
              isBillingV3Company,
              isCreateCompany,
              url: this.router.url,
            },
          });
          return of('');
        }
        const entitlementsToken = this.getEntitlementsToken();
        if (entitlementsToken) {
          return of(entitlementsToken);
        }
        this.logger.warn({
          message: 'entitlements_token_missing',
          data: { detail: 'No cached token, fetching fresh' },
        });
        return this.fetchEntitlementsToken(authToken);
      })
    );
  }

  isEntitlementsEnabled(): Observable<boolean> {
    return combineLatest([this.store.select(selectIsBillingV3Company)]).pipe(
      map(([isBillingV3Company]) => isBillingV3Company && !this.router.url?.startsWith('/create-company'))
    );
  }

  parseEntitlementsToken(entitlementsToken?: EntitlementsToken): Entitlements {
    if (!entitlementsToken) entitlementsToken = this.tokenService.getEntitlementsTokenDecoded();

    if (!entitlementsToken?.scope) return { scopes: [], expiration: null, companyId: '' };

    const scopes = entitlementsToken.scope.split(',').map((scope: string) => {
      const [name, permission] = scope.split(':');
      return { name, permission } as EntitlementScope;
    });

    return {
      scopes,
      expiration: new Date(entitlementsToken.exp * 1000),
      companyId: entitlementsToken.sub,
    };
  }

  private getHeaders(authToken: string): HttpHeaders {
    return new HttpHeaders({
      'content-type': 'application/json; charset=utf-8',
      Authorization: `Bearer ${authToken}`,
    });
  }
}
